import { useSyncExternalStore } from 'react';

/**
 * Analytics consent for the public marketing pages.
 *
 * EU/EEA, UK and Swiss rules need opt-in before analytics cookies are set.
 * The browser can't see the visitor's country, so visitors whose time zone
 * is in Europe get the cookie banner and Google Analytics doesn't load until
 * they accept. Google Consent Mode is also told to default to "denied" in
 * CONSENT_REGIONS (see GoogleAnalytics.tsx), which covers anyone in those
 * countries whose time zone says otherwise. Everyone else is tracked without
 * a banner, as the privacy policy describes.
 */

export type ConsentChoice = 'granted' | 'denied';

export interface ConsentState {
  choice: ConsentChoice | null;
  bannerOpen: boolean;
}

const STORAGE_KEY = 'hexdi-analytics-consent';

/** ISO 3166-1 codes where Consent Mode defaults analytics to denied: EU, EEA, UK, Switzerland. */
export const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO', 'GB', 'CH',
];

const EUROPEAN_ATLANTIC_ZONES = [
  'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Faroe', 'Atlantic/Madeira', 'Atlantic/Reykjavik',
];

export function inConsentTimeZone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    return tz.startsWith('Europe/') || EUROPEAN_ATLANTIC_ZONES.includes(tz);
  } catch {
    return false;
  }
}

/** Whether Google Analytics may load for this visitor right now. */
export function analyticsAllowed(choice: ConsentChoice | null): boolean {
  return choice === 'granted' || (choice === null && !inConsentTimeZone());
}

function readChoice(): ConsentChoice | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

const SERVER_STATE: ConsentState = { choice: null, bannerOpen: false };
let state: ConsentState | null = null;
const listeners = new Set<() => void>();

function current(): ConsentState {
  if (!state) {
    const choice = readChoice();
    state = { choice, bannerOpen: choice === null && inConsentTimeZone() };
  }
  return state;
}

function emit(next: ConsentState) {
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setConsent(choice: ConsentChoice) {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
  emit({ choice, bannerOpen: false });
}

/** Reopens the banner from the footer's "Cookie settings" link. */
export function openConsentBanner() {
  emit({ ...current(), bannerOpen: true });
}

export function useConsent(): ConsentState {
  return useSyncExternalStore(subscribe, current, () => SERVER_STATE);
}

/** Test-only: forget the cached state so the next read starts fresh. */
export function resetConsentForTests() {
  state = null;
  listeners.clear();
}
